import { bunja } from 'bunja';

import { KycClientStatusEnum } from '@flipster/apis/v1/kyc/types';
import { UserBlockServiceEnum, UserBlockServiceType } from '@flipster/apis/v1/user/types';
import { FormattedMessage } from '@flipster/i18n';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { RouterScope } from '@flipster/react/providers/route';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import segment, { WalletEvent } from '@flipster/utils/segment';

import { kycBunja } from '../../../kyc';
import { userBlockBunja } from '../../../user-block/state';
import { zendesk } from '../../../zendesk/url';
import { WalletModalType } from '../core/state';

const UserBlockMapper: Record<'deposit' | 'convert' | 'withdraw', UserBlockServiceType> = {
  withdraw: UserBlockServiceEnum.Withdrawal,
  deposit: UserBlockServiceEnum.Deposit,
  convert: UserBlockServiceEnum.Conversion,
};

export const accountValidationBunja = bunja(() => {
  const router = bunja.use(RouterScope);
  const store = bunja.use(JotaiStoreScope);
  // To get fresh data by awaiting promiseAtom, you should not use data atom - it is not updated synchronously.
  const { promiseAtom: kycPromiseAtom } = bunja.use(kycBunja);
  const { promiseAtom: userBlockPromiseAtom } = bunja.use(userBlockBunja);
  async function fetchKycInfo() {
    const { promise } = store.get(kycPromiseAtom);
    return await promise; // catch here?
  }
  async function checkKycAndOpenAlertIfNeeded() {
    const kycInfo = await fetchKycInfo();
    const isAchieved =
      kycInfo!.currentKYCLevel >= kycInfo!.desirableKYCLevel && kycInfo?.clientStatus === KycClientStatusEnum.Active;
    if (!isAchieved) {
      dialog.open({
        title: {
          label: <FormattedMessage id='Assets.r250401.Identity_verification' />,
        },
        content: <FormattedMessage id='Assets.r250401.Identity_verification_required_description' />,
        buttons: {
          primary: (
            <Dialog.Button
              hierarchy='primary'
              label={<FormattedMessage id='Assets.r250401.Verify_now' />}
              onClick={() => {
                segment.track(WalletEvent.KycVerificationRequireClick, { kyc_level: kycInfo.currentKYCLevel });
                router.push('/user/kyc');
              }}
            />
          ),
          secondary: (
            <Dialog.Button
              hierarchy='secondary'
              color='neutral'
              label={<FormattedMessage id='Reward_hub.r250213.Close' />}
              onClick={() => {
                segment.track(WalletEvent.VerifyCancelButton);
              }}
            />
          ),
        },
      });
    }
    return isAchieved;
  }
  async function checkUserBlockAndOpenAlertIfNeeded(type: WalletModalType) {
    const { promise: userBlockPromise } = store.get(userBlockPromiseAtom);
    const userBlockList = await userBlockPromise; // catch here?
    const blockInfo = userBlockList?.[UserBlockMapper[type]];
    if (blockInfo) {
      dialog.open({
        title: {
          label: <FormattedMessage id='Assets.r250401.Account_suspended' />,
        },
        content: <FormattedMessage id='Assets.r250401.Account_suspended_description' />,
        buttons: {
          primary: (
            <Dialog.Button
              hierarchy='primary'
              label={<FormattedMessage id='Assets.r250401.Contact_support' />}
              onClick={() => {
                window.open(zendesk.getUrl('RequestNew'), '_blank', 'noopener');
              }}
            />
          ),
          secondary: (
            <Dialog.Button
              hierarchy='secondary'
              color='neutral'
              label={<FormattedMessage id='Reward_hub.r250213.Close' />}
            />
          ),
        },
      });
      return false;
    }
    return true;
  }
  return { checkKycAndOpenAlertIfNeeded, checkUserBlockAndOpenAlertIfNeeded };
});
accountValidationBunja.debugLabel = 'accountValidationBunja';
